import { Logo } from "@/components/Logo";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, BookOpen, Star, Heart } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "parent" ? "/portal" : "/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Selamat datang di Bimbel Gemma! 🌟");
      navigate(res.user.role === "parent" ? "/portal" : "/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden bg-gradient-to-br from-orange-500 via-amber-500 to-orange-400">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 2px, transparent 2px), radial-gradient(circle at 70% 60%, white 3px, transparent 3px)", backgroundSize: "60px 60px" }} />
        <div className="relative z-10 flex flex-col justify-center px-14 text-white">
          <Logo size="lg" light className="mb-10" />
          <h1 className="font-display text-5xl font-bold leading-tight mb-4">Selamat Datang!</h1>
          <p className="text-white/90 text-lg max-w-md mb-10">Belajar Ceria, Berprestasi Bersama. Sistem manajemen les untuk anak TPA, TK &amp; SD.</p>
          <div className="space-y-4">
            {[
              { icon: BookOpen, t: "Jurnal belajar & absensi harian" },
              { icon: Star, t: "Kartu prestasi & sertifikat" },
              { icon: Heart, t: "Notifikasi WhatsApp ke wali murid" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center"><f.icon className="w-5 h-5" /></div>
                <span className="text-white/90">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 bg-[#FAF8F5]">
        <div className="w-full max-w-md animate-float-in">
          <div className="md:hidden mb-8"><Logo size="md" /></div>
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-1">Masuk Akun</h2>
          <p className="text-slate-500 mb-8">Silakan masuk untuk mengelola bimbel.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-semibold">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com"
                className="mt-1.5 h-12 rounded-xl border-amber-200 focus-visible:ring-orange-400" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-semibold">Kata Sandi</Label>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="mt-1.5 h-12 rounded-xl border-amber-200 focus-visible:ring-orange-400" />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold text-base shadow-lg shadow-orange-200 transition-colors">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
